import React, { useEffect, useRef, useState } from 'react';
import FeatherIcon from 'feather-icons-react';
import { SkillMapService, type BodyRegion } from '../services/skillMapService';
import '../styles/body-part-picker.css';

// Body-part tagging for a skill (AXISREF498).
// Suggestions come from the description and field name. They stay applied until the
// author changes the selection; after that the author's choice wins and is saved as
// manual tags. onChange reports null while untouched so callers only send body_regions
// when the author actually chose them.

interface BodyPartPickerProps {
  description: string;
  fieldName?: string;
  ruleId?: string;
  onChange: (selection: string[] | null) => void;
}

const DEBOUNCE_MS = 400;

function sameSet(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every(code => b.includes(code));
}

export const BodyPartPicker: React.FC<BodyPartPickerProps> = ({ description, fieldName, ruleId, onChange }) => {
  const [regions, setRegions] = useState<BodyRegion[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [suggested, setSuggested] = useState<string[]>([]);
  const [touched, setTouched] = useState(false);
  const [followSuggestions, setFollowSuggestions] = useState(true);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [attempt, setAttempt] = useState(0);
  const initialised = useRef(false);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    let cancelled = false;
    const isFirstLoad = !initialised.current;
    const timer = window.setTimeout(
      async () => {
        try {
          setStatus(prev => (prev === 'ready' ? 'ready' : 'loading'));
          const preview = await SkillMapService.previewBodyRegions({
            description,
            field_name: fieldName || undefined,
            rule_id: isFirstLoad ? ruleId : undefined,
          });
          if (cancelled) return;
          const suggestion = preview.suggested.map(s => s.code);
          setRegions(preview.regions);
          setSuggested(suggestion);
          if (isFirstLoad) {
            initialised.current = true;
            if (preview.current) {
              setSelected(preview.current.regions.map(r => r.code));
              setFollowSuggestions(!preview.current.has_manual);
            } else {
              setSelected(suggestion);
            }
          } else if (followSuggestions) {
            setSelected(suggestion);
          }
          setStatus('ready');
        } catch (err) {
          console.error('Failed to preview body parts:', err);
          if (!cancelled) setStatus('error');
        }
      },
      isFirstLoad ? 0 : DEBOUNCE_MS,
    );
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
    // followSuggestions is read at fire time; re-running on it would refetch needlessly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [description, fieldName, ruleId, attempt]);

  useEffect(() => {
    onChangeRef.current(touched ? selected : null);
  }, [touched, selected]);

  const toggle = (code: string) => {
    setTouched(true);
    setFollowSuggestions(false);
    setSelected(prev => (prev.includes(code) ? prev.filter(c => c !== code) : [...prev, code]));
  };

  const useSuggestion = () => {
    setTouched(true);
    setFollowSuggestions(true);
    setSelected(suggested);
  };

  const labelFor = (code: string) => regions.find(r => r.code === code)?.label ?? code;
  const ordered = (codes: string[]) =>
    [...codes].sort(
      (a, b) =>
        (regions.find(r => r.code === a)?.sort_order ?? 0) - (regions.find(r => r.code === b)?.sort_order ?? 0),
    );
  const suggestionText = ordered(suggested).map(labelFor).join(', ');

  let hint: React.ReactNode;
  if (status === 'error' && regions.length === 0) {
    hint = (
      <>
        Body parts couldn't be loaded.{' '}
        <button type="button" className="body-part-picker-use" onClick={() => setAttempt(n => n + 1)}>
          Try again
        </button>
      </>
    );
  } else if (status === 'error') {
    hint = "Couldn't check the description for body parts. You can still choose them yourself.";
  } else if (!suggested.length) {
    hint = 'No body part mentioned in the description.';
  } else if (followSuggestions && sameSet(selected, suggested)) {
    hint = <>Selected from the description: {suggestionText}. Change it if that's wrong.</>;
  } else if (!sameSet(selected, suggested)) {
    hint = (
      <>
        The description mentions {suggestionText}.{' '}
        <button type="button" className="body-part-picker-use" onClick={useSuggestion}>
          Use suggestion
        </button>
      </>
    );
  }

  return (
    <fieldset className="body-part-picker" data-testid="body-part-picker">
      <legend>
        Body parts <span className="body-part-picker-optional">(Optional)</span>
      </legend>
      <p className="body-part-picker-help">
        Choose the body parts this skill is written for. Leave it empty if it applies to every referral.
      </p>

      {regions.length === 0 && status !== 'error' ? (
        <div className="body-part-picker-options" aria-hidden="true">
          {Array.from({ length: 11 }).map((_, i) => (
            <span key={i} className="body-part-picker-skeleton" />
          ))}
        </div>
      ) : (
        <div className="body-part-picker-options">
          {regions.map(region => {
            const checked = selected.includes(region.code);
            return (
              <label key={region.code} className={`body-part-option${checked ? ' checked' : ''}`}>
                <input type="checkbox" checked={checked} onChange={() => toggle(region.code)} />
                {checked && <FeatherIcon icon="check" size={14} aria-hidden="true" />}
                <span>{region.label}</span>
              </label>
            );
          })}
        </div>
      )}

      <p className="body-part-picker-status" aria-live="polite">
        {selected.length === 0 && status === 'ready' ? (
          <strong>Everyday skill: applies to every referral. </strong>
        ) : null}
        {hint}
      </p>
    </fieldset>
  );
};
